// src/hooks/useCanvasSetup.js
// ---------------------------------------------------------------------------
// Hook: creates a SINGLE unified Fabric.js wraparound canvas that holds
// all three surfaces (back + spine + front) in one coordinate space.
//
// Layout (left to right):
//   Back  [0 .. faceW]
//   Spine [faceW .. faceW+spineW]
//   Front [faceW+spineW .. 2*faceW+spineW]
//
// View switching is handled by the caller via viewportTransform + setDimensions.
// This hook only initialises the canvas, fetches version data, hydrates with
// the wraparound design (or migrates from 3 legacy per-face designs), and
// exposes the face dimensions so the caller can compute views.
// ---------------------------------------------------------------------------
import { useEffect, useRef, useState } from 'react';
import * as fabric from 'fabric';
import { API_BASE } from '../utils/api';

// ---------------------------------------------------------------------------
// Extend fabric.Textbox to support a visible box border that is separate from
// the character-level `stroke` (letter outlines).  Two custom properties:
//   _boxStroke      – CSS color string for the border
//   _boxStrokeWidth – numeric pixel width of the border
// These are included in toObject() so they survive canvas.toJSON() / loadFromJSON().
// ---------------------------------------------------------------------------
(function patchTextboxBoxBorder() {
  // Helper: convert hex to RGB
  const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') return { r: 0, g: 0, b: 0 };
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const n = parseInt(h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  };

  // Helper: convert RGB to hex
  const rgbToHex = (r, g, b) => {
    const to = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
  };

  // Helper: convert hex to HSL
  const hexToHsl = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    const rn = r / 255, gn = g / 255, bn = b / 255;
    const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case rn: h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6; break;
        case gn: h = ((bn - rn) / d + 2) / 6; break;
        case bn: h = ((rn - gn) / d + 4) / 6; break;
      }
    }
    return { h, s, l };
  };

  // Helper: convert HSL to hex
  const hslToHex = (h, s, l) => {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = Math.round(hue2rgb(p, q, h + 1/3) * 255);
    const g = Math.round(hue2rgb(p, q, h) * 255);
    const b = Math.round(hue2rgb(p, q, h - 1/3) * 255);
    return rgbToHex(r, g, b);
  };

  // Helper: adjust brightness of color using HSL
  const adjustBrightness = (hex, amount) => {
    const { h, s, l } = hexToHsl(hex);
    const adjustment = amount / 100 * 0.5; // brightness adjustment range: -50% to +50% of lightness
    const newL = Math.max(0, Math.min(1, l + adjustment));
    return hslToHex(h, s, newL);
  };
  // Guard against double-patching on hot-reload: save the true originals once on the
  // prototype itself, then always base the patches on those saved references.
  if (!fabric.Textbox.prototype._patchSavedRenderBg) {
    fabric.Textbox.prototype._patchSavedRenderBg       = fabric.Textbox.prototype._renderBackground;
    fabric.Textbox.prototype._patchSavedRender         = fabric.Textbox.prototype._render;
    fabric.Textbox.prototype._patchSavedToObject       = fabric.Textbox.prototype.toObject;
    fabric.Textbox.prototype._patchSavedGetCacheDims   = fabric.Textbox.prototype._getCacheCanvasDimensions;
  }

  // Expand the object-cache canvas when a reflection is active so the reflected
  // copy (which renders outside the normal bounding box) is not clipped away.
  // For left/right directions we base the expansion on the actual text content
  // width (calcTextWidth) so the reflection stays near the visible glyphs and
  // does not push the cache into the fabric size limits.
  const _origGetCacheDims = fabric.Textbox.prototype._patchSavedGetCacheDims;
  fabric.Textbox.prototype._getCacheCanvasDimensions = function () {
    const dims = _origGetCacheDims.call(this);
    if (this._reflection && this._reflection.on) {
      const gap = this._reflection.gap ?? 0;
      const dir = this._reflection.direction || 'below';
      if (dir === 'left' || dir === 'right') {
        const textW = (typeof this.calcTextWidth === 'function' ? this.calcTextWidth() : null) || this.width;
        // worst-case: the reflection center is at 2*edge from textbox center, plus a full
        // textbox half-width beyond that.  add textW + this.width/2 on each side.
        dims.width  += (textW + this.width / 2 + gap + 4) * 2 * dims.zoomX;
        dims.height += (this.height + gap + 4) * 2 * dims.zoomY;
      } else {
        dims.width  += (this.width  + gap + 4) * 2 * dims.zoomX;
        dims.height += (this.height + gap + 4) * 2 * dims.zoomY;
      }
    }
    return dims;
  };

  const _origRenderBackground = fabric.Textbox.prototype._patchSavedRenderBg;
  fabric.Textbox.prototype._renderBackground = function (ctx) {
    _origRenderBackground.call(this, ctx);
    if (this._boxStroke && this._boxStrokeWidth > 0) {
      ctx.save();
      ctx.strokeStyle = this._boxStroke;
      ctx.lineWidth   = this._boxStrokeWidth;
      ctx.strokeRect(-this.width / 2, -this.height / 2, this.width, this.height);
      ctx.restore();
    }
  };

  // Reflection: flipped mirror copy with optional special effects.
  // _reflection props: on, direction, color, brightness, amount, fade, size, gap, blur,
  //   blendMode, rainbow, gradientColor1, gradientColor2, glitch, wave, waveFreq
  let _refl_tempCanvas  = null;
  let _refl_waveCanvas  = null;
  let _refl_glitchCanvas = null;
  const COLOR_STYLE_KEYS = new Set(['fill', 'textBackgroundColor']);
  const stripColorStyles = (styles) => {
    const out = {};
    for (const line in styles) {
      const lineOut = {};
      for (const char in styles[line]) {
        const charStyle = { ...styles[line][char] };
        COLOR_STYLE_KEYS.forEach(k => delete charStyle[k]);
        if (Object.keys(charStyle).length > 0) lineOut[char] = charStyle;
      }
      if (Object.keys(lineOut).length > 0) out[line] = lineOut;
    }
    return out;
  };

  const _origRender = fabric.Textbox.prototype._patchSavedRender;
  fabric.Textbox.prototype._render = function (ctx) {
    const refl = this._reflection;

    // When reflection direction changes, clear the full cache canvas first so no
    // ghost pixels from the previous direction survive into this render.
    if (refl?.on) {
      const newDir = refl.direction || 'below';
      if (this._lastReflDir !== undefined && this._lastReflDir !== newDir) {
        const cc = ctx.canvas;
        if (cc) {
          const t = ctx.getTransform();
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.clearRect(0, 0, cc.width, cc.height);
          ctx.setTransform(t.a, t.b, t.c, t.d, t.e, t.f);
        }
      }
      this._lastReflDir = newDir;
    }

    _origRender.call(this, ctx);
    if (!refl || !refl.on) return;

    const textH    = typeof this.calcTextHeight === 'function' ? this.calcTextHeight() : this.height;
    const amount   = Math.max(0, Math.min(100, refl.amount   ?? 50))  / 100;
    const gap      = Math.min(50,              refl.gap       ?? 0);
    const size     = Math.max(0.01, Math.min(100, refl.size  ?? 100)) / 100;
    const blur     = Math.max(0,               refl.blur      ?? 0);
    const fade     = !!refl.fade;
    const blendMode = refl.blendMode || 'source-over';
    const rainbow   = !!refl.rainbow;
    const useGrad   = !rainbow && !!(refl.gradientColor1 || refl.gradientColor2);
    const glitch    = Math.max(0, Math.min(20,  refl.glitch   ?? 0));
    const waveAmp   = Math.max(0, Math.min(30,  refl.wave     ?? 0));
    const waveFreq  = Math.max(0.5, Math.min(20, refl.waveFreq ?? 3));
    const needsTemp = fade || rainbow || useGrad || waveAmp > 0 || glitch > 0 || blendMode !== 'source-over';

    // Base fill: gradient/rainbow override the colour entirely (applied post-render);
    // for solid-colour mode keep the existing colour/brightness logic.
    const origFill   = this.fill;
    const origStyles = this.styles;
    const hadStyles  = origStyles && Object.keys(origStyles).length > 0;
    let reflFill = origFill;
    if (!rainbow && !useGrad) {
      if (refl.color) {
        reflFill = refl.color;
        if (refl.brightness) reflFill = adjustBrightness(this._reflectionOriginalColor || refl.color, refl.brightness);
      } else if (refl.brightness) {
        reflFill = adjustBrightness(origFill, refl.brightness);
      }
    }

    // Compute direction-specific transform and clip/gradient geometry.
    // Each direction: translate puts reflection adjacent to text (+ gap),
    // scale flips the axis, clip/gradient crop to size% from the near edge.
    const dir = refl.direction || 'below';
    const w = this.width;
    const h = this.height;
    let translateX, translateY, scaleX, scaleY;
    let gradX1, gradY1, gradX2, gradY2;
    let clipX, clipY, clipW, clipH;

    if (dir === 'above') {
      translateX = 0;        translateY = -(h + gap);
      scaleX = 1;            scaleY = -1;
      // near = top of box (-h/2), far = bottom of text content
      gradX1 = 0;            gradY1 = -h / 2;
      gradX2 = 0;            gradY2 = -h / 2 + textH * size;
      clipX = -w / 2;        clipY = -h / 2;
      clipW = w;             clipH = textH * size;
    } else if (dir === 'right' || dir === 'left') {
      // Mirror axis = actual text edge (not textbox edge).
      // For left-aligned text the text sits at -w/2; using textbox center as
      // axis would land the reflection on the wrong side of the visible glyphs.
      const textW = (typeof this.calcTextWidth === 'function' ? this.calcTextWidth() : null) || w;
      const align = this.textAlign || 'left';
      let textLeftEdge, textRightEdge;
      if (align === 'center') {
        textLeftEdge = -textW / 2;
        textRightEdge =  textW / 2;
      } else if (align === 'right') {
        textLeftEdge = w / 2 - textW;
        textRightEdge = w / 2;
      } else { // 'left', 'justify'
        textLeftEdge = -w / 2;
        textRightEdge = -w / 2 + textW;
      }

      scaleX = -1; scaleY = 1; translateY = 0;
      if (dir === 'right') {
        // mirror axis at text right edge; reflection appears to the right.
        // After scaleX=-1, physical-right maps to negative user-x, so gradient/clip
        // coords run from nearX toward nearX-textW (negative user-x direction).
        translateX = 2 * textRightEdge + gap;
        const nearX = textRightEdge + gap;
        gradX1 = nearX;                gradY1 = 0;
        gradX2 = nearX - textW * size; gradY2 = 0;
        clipX = nearX - textW * size; clipY = -h / 2; clipW = textW * size; clipH = h;
      } else {
        // mirror axis at text left edge; reflection appears to the left.
        // After scaleX=-1, physical-left maps to positive user-x, so gradient/clip
        // coords run from nearX toward nearX+textW (positive user-x direction).
        translateX = 2 * textLeftEdge - gap;
        const nearX = textLeftEdge - gap;
        gradX1 = nearX;                gradY1 = 0;
        gradX2 = nearX + textW * size; gradY2 = 0;
        clipX = nearX; clipY = -h / 2; clipW = textW * size; clipH = h;
      }
    } else {
      // 'below' (default)
      translateX = 0;        translateY = 2 * textH - h + gap;
      scaleX = 1;            scaleY = -1;
      // near = bottom of text content, far = top of text content
      gradX1 = 0;            gradY1 = -h / 2 + textH;
      gradX2 = 0;            gradY2 = -h / 2 + textH - textH * size;
      clipX = -w / 2;        clipY = -h / 2 + textH - textH * size;
      clipW = w;             clipH = textH * size;
    }

    ctx.save();
    ctx.translate(translateX, translateY);
    ctx.scale(scaleX, scaleY);

    this.fill = reflFill;
    if (hadStyles) this.styles = stripColorStyles(origStyles);
    try {
      if (!needsTemp) {
        // ── Fast path: no compositing effects ──────────────────────────────
        if (size < 1) {
          ctx.beginPath();
          ctx.rect(clipX, clipY, clipW, clipH);
          ctx.clip();
        }
        ctx.globalAlpha = amount;
        if (blur > 0) ctx.filter = `blur(${blur}px)`;
        _origRender.call(this, ctx);
        if (blur > 0) ctx.filter = 'none';
      } else {
        // ── Full effects path ───────────────────────────────────────────────
        const mc = ctx.canvas;
        const cw = mc.width, ch = mc.height;

        const ensureCanvas = (ref) => {
          if (!ref.c) ref.c = document.createElement('canvas');
          if (ref.c.width !== cw || ref.c.height !== ch) { ref.c.width = cw; ref.c.height = ch; }
          return ref.c;
        };
        const tmpRef   = { c: _refl_tempCanvas };
        const waveRef  = { c: _refl_waveCanvas };
        const glitchRef = { c: _refl_glitchCanvas };
        const tmpCanvas = ensureCanvas(tmpRef);
        _refl_tempCanvas = tmpCanvas;

        const tmp = tmpCanvas.getContext('2d');
        // Reset transform to identity BEFORE clearRect — the context retains the
        // previous render's reflection transform, so clearRect without this reset
        // only clears a transformed sub-region, leaving ghost pixels behind.
        tmp.setTransform(1, 0, 0, 1, 0, 0);
        tmp.clearRect(0, 0, cw, ch);
        const t = ctx.getTransform();
        tmp.setTransform(t.a, t.b, t.c, t.d, t.e, t.f);

        // 1. Base render
        if (blur > 0) tmp.filter = `blur(${blur}px)`;
        _origRender.call(this, tmp);
        if (blur > 0) tmp.filter = 'none';

        // 2. Gradient / rainbow colour overlay (source-in replaces rendered colour)
        if (rainbow || useGrad) {
          const cg = tmp.createLinearGradient(gradX1, gradY1, gradX2, gradY2);
          if (rainbow) {
            ['#ff0000','#ff7700','#ffee00','#00cc44','#0066ff','#8800ff','#ff0088']
              .forEach((c, i, a) => cg.addColorStop(i / (a.length - 1), c));
          } else {
            cg.addColorStop(0, refl.gradientColor1 || origFill);
            cg.addColorStop(1, refl.gradientColor2 || origFill);
          }
          tmp.globalCompositeOperation = 'source-in';
          tmp.fillStyle = cg;
          // fillRect in object-local space; source-in only affects rendered pixels
          tmp.fillRect(-w * 5, -h * 5, w * 10, h * 10);
          tmp.globalCompositeOperation = 'source-over';
        }

        // 3. Fade gradient mask OR hard size clip
        if (fade) {
          const fg = tmp.createLinearGradient(gradX1, gradY1, gradX2, gradY2);
          fg.addColorStop(0, 'rgba(0,0,0,1)');
          fg.addColorStop(1, 'rgba(0,0,0,0)');
          tmp.globalCompositeOperation = 'destination-in';
          tmp.fillStyle = fg;
          tmp.fillRect(clipX, clipY, clipW, clipH);
          tmp.globalCompositeOperation = 'source-over';
        } else if (size < 1) {
          tmp.globalCompositeOperation = 'destination-in';
          tmp.fillStyle = 'rgba(0,0,0,1)';
          tmp.fillRect(clipX, clipY, clipW, clipH);
          tmp.globalCompositeOperation = 'source-over';
        }

        // 4. Wave distortion — scanline shift along the near-far axis
        let finalCanvas = tmpCanvas;
        if (waveAmp > 0) {
          const waveCanvas = ensureCanvas(waveRef);
          _refl_waveCanvas = waveCanvas;
          const wCtx = waveCanvas.getContext('2d');
          wCtx.clearRect(0, 0, cw, ch);
          const isHoriz = dir === 'left' || dir === 'right';
          if (isHoriz) {
            for (let y = 0; y < ch; y++) {
              const dx = waveAmp * Math.sin((y / ch) * waveFreq * 2 * Math.PI);
              wCtx.drawImage(tmpCanvas, 0, y, cw, 1, dx, y, cw, 1);
            }
          } else {
            for (let x = 0; x < cw; x++) {
              const dy = waveAmp * Math.sin((x / cw) * waveFreq * 2 * Math.PI);
              wCtx.drawImage(tmpCanvas, x, 0, 1, ch, x, dy, 1, ch);
            }
          }
          finalCanvas = waveCanvas;
        }

        // 5. Composite onto main cache canvas
        ctx.resetTransform();

        if (glitch > 0) {
          // Chromatic aberration: draw R/G/B channel copies with lateral offsets
          const gCanvas = ensureCanvas(glitchRef);
          _refl_glitchCanvas = gCanvas;
          const gCtx = gCanvas.getContext('2d');

          const drawChannel = (color, dx) => {
            gCtx.clearRect(0, 0, cw, ch);
            gCtx.globalCompositeOperation = 'source-over';
            gCtx.drawImage(finalCanvas, dx, 0);
            gCtx.globalCompositeOperation = 'source-in';
            gCtx.fillStyle = color;
            gCtx.fillRect(0, 0, cw, ch);
            ctx.globalCompositeOperation = 'screen';
            ctx.globalAlpha = amount * 0.75;
            ctx.drawImage(gCanvas, 0, 0);
          };

          ctx.globalCompositeOperation = blendMode;
          ctx.globalAlpha = amount * 0.6;
          ctx.drawImage(finalCanvas, 0, 0);   // green / main pass
          drawChannel('red',  -glitch);        // red:  shifted left
          drawChannel('blue',  glitch);        // blue: shifted right
        } else {
          ctx.globalCompositeOperation = blendMode;
          ctx.globalAlpha = amount;
          ctx.drawImage(finalCanvas, 0, 0);
        }
      }
    } finally {
      this.fill   = origFill;
      if (hadStyles) this.styles = origStyles;
      ctx.restore();
    }
  };

  const _origToObject = fabric.Textbox.prototype._patchSavedToObject;
  fabric.Textbox.prototype.toObject = function (propertiesToInclude) {
    return _origToObject.call(this, [
      '_boxStroke', '_boxStrokeWidth', '_collabId',
      '_effectType', '_reflection', '_glowBrightness', '_glowOriginalColor', '_reflectionBrightness', '_reflectionOriginalColor', '_shadeAmount', '_origShadeFill',
      ...(propertiesToInclude || []),
    ]);
  };
}());

// ---------------------------------------------------------------------------
// Ensure _collabId (annotation thread ID) and other custom props are included
// in canvas.toJSON() for ALL object types (Rect, Circle, Image, Path, etc.).
// Fabric v6 uses FabricObject.customProperties for this — it's merged into
// propertiesToSerialize inside toObject() automatically.
// NOTE: 'name' and '_locked' are also registered here for safety, even though
// Fabric may already handle 'name' natively in some versions.
// ---------------------------------------------------------------------------
(function registerCustomProperties() {
  const base = fabric.FabricObject;
  if (!base) return;
  if (!Array.isArray(base.customProperties)) base.customProperties = [];
  const toAdd = ['_collabId', '_label', 'name', '_locked'];
  for (const prop of toAdd) {
    if (!base.customProperties.includes(prop)) base.customProperties.push(prop);
  }
}());

// -- Dimension lookup tables --------------------------------------------------
// All sizes at exactly 66 px/inch. realW/realH in inches for ruler conversion.
const BOOK_SIZE_DIMS = {
  half_letter: { w: 363, h: 561, realW: 5.5,   realH: 8.5   },
  trade_6x9:  { w: 396, h: 594, realW: 6,     realH: 9     },
  a5:         { w: 385, h: 546, realW: 5.83,  realH: 8.27  },
  pocket_4x7: { w: 281, h: 454, realW: 4.25,  realH: 6.875 },
  us_letter:  { w: 561, h: 726, realW: 8.5,   realH: 11    },
};

const BINDING_SPINE_W = {
  perfect:       100,
  case_bound:    110,
  coil:          60,
  wire:          55,
  saddle_stitch: 12,
};

// -- Migration helper ---------------------------------------------------------
/**
 * Merge three legacy per-face designs into a single wraparound design.
 * Back objects stay at their original x; spine objects shift right by faceW;
 * front objects shift right by faceW + spineW.
 */
function migrateToWraparound(coverDesign, spineDesign, backDesign, faceW, spineW) {
  const merged = {
    version: '6.0.0',
    objects: [],
    backgroundColor: '#ffffff',
  };

  const parse = (d) => {
    if (!d) return null;
    if (typeof d === 'string') {
      try { return JSON.parse(d); } catch { return null; }
    }
    return d;
  };

  const back = parse(backDesign);
  const spine = parse(spineDesign);
  const front = parse(coverDesign);

  // Back objects (x unchanged - back starts at 0)
  if (back && back.objects) {
    for (const obj of back.objects) {
      merged.objects.push({ ...obj });
    }
    if (back.hasIsbn) merged.hasIsbn = true;
  }

  // Spine objects (shift right by faceW)
  if (spine && spine.objects) {
    for (const obj of spine.objects) {
      merged.objects.push({ ...obj, left: (obj.left || 0) + faceW });
    }
  }

  // Front objects (shift right by faceW + spineW)
  if (front && front.objects) {
    for (const obj of front.objects) {
      merged.objects.push({ ...obj, left: (obj.left || 0) + faceW + spineW });
    }
  }

  return merged;
}

// -- Hook ---------------------------------------------------------------------
/**
 * @param {string} draftId
 * @param {string|undefined} urlVersionId
 */
export default function useCanvasSetup(draftId, urlVersionId) {
  const canvasRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [currentVersionId, setCurrentVersionId] = useState(null);
  const [activeVersionName, setActiveVersionName] = useState('');
  const [isAuthor, setIsAuthor] = useState(false);
  const [collaboratorRole, setCollaboratorRole] = useState(null);
  const [versionData, setVersionData] = useState({});
  const [isHydrating, setIsHydrating] = useState(true);
  const [hasIsbn, setHasIsbn] = useState(false);
  const [hasCoverImage, setHasCoverImage] = useState(false);

  // Dynamic face dimensions - updated once version data arrives
  const [faceDims, setFaceDims] = useState({
    faceW: 360,
    faceH: 540,
    spineW: 100,
  });

  const readOnly = collaboratorRole === 'view_only' && !isAuthor;

  const DEFAULT_FACE_W = 360;
  const DEFAULT_FACE_H = 540;
  const DEFAULT_SPINE_W = 100;
  const DEFAULT_TOTAL_W = DEFAULT_FACE_W * 2 + DEFAULT_SPINE_W; // 820

  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    const el = canvasRef.current;
    if (!el) return;

    el.width = DEFAULT_TOTAL_W;
    el.height = DEFAULT_FACE_H;

    const c = new fabric.Canvas(el, {
      width: DEFAULT_TOTAL_W,
      height: DEFAULT_FACE_H,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
      renderOnAddRemove: true,
    });

    c.backgroundColor = '#ffffff';
    c.requestRenderAll();
    // Hydration flag on the CANVAS, not only in React state.
    //
    // `isHydrating` is state, so the value a canvas event handler sees can lag
    // a render behind — and `applyFaceClip` runs on `object:added`, which fires
    // during a load. When it saw a stale `false` it clipped freshly-loaded
    // artwork to whatever face was on screen, silently re-scoping an object the
    // author had set to "Entire Cover". A property on the canvas is written and
    // read synchronously, so there is no window for it to be wrong.
    c._rvHydrating = true;
    setCanvas(c);
    didInit.current = true;

    let cancelled = false;

    (async () => {
      try {
        const t =
          localStorage.getItem('rv_access_token') ||
          localStorage.getItem('access_token') || '';

        const versionsRes = await fetch(
          `${API_BASE}/api/drafts/${draftId}/cover/versions?includeDesign=true`,
          { credentials: 'omit', headers: t ? { Authorization: `Bearer ${t}` } : {} },
        );

        let json = null;
        let targetVersionId = urlVersionId;
        let versionCollabRole = null;
        let roleAuthor = false;
        let appliedDims = null; // { faceW, faceH, spineW } from applyVersion — used by post-load cleanup since faceDims state is stale here

        if (versionsRes.ok) {
          const versionsData = await versionsRes.json();
          roleAuthor = versionsData.isAuthor || false;

          if (!targetVersionId) {
            targetVersionId = versionsData.activeVersionId;
          }

          const targetVersion = (versionsData.versions || []).find(
            (v) => v.id === targetVersionId,
          );

          setIsAuthor(versionsData.isAuthor || false);

          const applyVersion = (ver) => {
            if (cancelled) return;
            versionCollabRole = ver.collaboratorRole || null;
            setCollaboratorRole(versionCollabRole);
            setActiveVersionName(ver.name || '');
            setCurrentVersionId(ver.id);
            setHasCoverImage(!!ver.coverImage);
            setVersionData({
              name: ver.name,
              coverStatus: ver.coverStatus,
              authorFeedback: ver.authorFeedback,
              coverReviewNotes: ver.coverReviewNotes,
              criteria: ver.criteria || '',
              coverType: ver.coverType || null,
              paperType: ver.paperType || null,
              bookSize: ver.bookSize || null,
              lamination: ver.lamination || null,
              bindingType: ver.bindingType || null,
              collaborators: ver.collaborators || [],
            });

            // -- Compute face dimensions from print specs --
            const sizeKey = ver.bookSize || 'half_letter';
            const dims = BOOK_SIZE_DIMS[sizeKey] || BOOK_SIZE_DIMS.half_letter;
            const bindKey = ver.bindingType || 'perfect';
            // spineWidthPx is set when the version was seeded from a /quote print spec — it
            // is derived from the real page count and paper caliper, so it beats the flat
            // per-binding constant. Versions without one keep the old behaviour, and a
            // custom thickness saved in the canvas JSON still overrides both (see below).
            const spW = ver.spineWidthPx || BINDING_SPINE_W[bindKey] || 100;
            const fW = dims.w;
            const fH = dims.h;
            const totalW = fW * 2 + spW;

            // Resize canvas to match print specs
            if (c.getWidth() !== totalW || c.getHeight() !== fH) {
              c.setDimensions({ width: totalW, height: fH });
              c.requestRenderAll();
            }
            setFaceDims({ faceW: fW, faceH: fH, spineW: spW });
            appliedDims = { faceW: fW, faceH: fH, spineW: spW };

            // -- Choose design source --
            // 1. Unified wraparound (new format)
            if (ver.wraparoundDesign) {
              json = ver.wraparoundDesign;
            }
            // 2. Migrate from 3 legacy per-face designs
            else if (ver.coverDesign || ver.spineDesign || ver.backDesign) {
              json = migrateToWraparound(
                ver.coverDesign, ver.spineDesign, ver.backDesign,
                fW, spW,
              );
            }
          };

          if (targetVersion) {
            applyVersion(targetVersion);
          } else if (versionsData.versions && versionsData.versions.length > 0) {
            applyVersion(versionsData.versions[0]);
          }
        }

        if (!json || cancelled) {
          c._rvHydrating = false;
          setIsHydrating(false);
          return;
        }

        json = typeof json === 'string' ? JSON.parse(json) : json;
        const bgColor = json.backgroundColor || json.background || '#ffffff';

        // -- Read hasIsbn flag from raw JSON --
        const foundIsbn = json.hasIsbn === true;
        setHasIsbn(foundIsbn);
        c._hasIsbn = foundIsbn;
        if (json.coverMargins) c._coverMargins = json.coverMargins;
        if (json.thicknessMode) c._thicknessMode = json.thicknessMode;

        // Override spine width from the saved canvas JSON — the user may have
        // set a custom thickness that differs from the binding-type default.
        const savedSpW = json.faceDims?.spineW;
        if (savedSpW > 0 && appliedDims && savedSpW !== appliedDims.spineW) {
          const newTotalW = appliedDims.faceW * 2 + savedSpW;
          c.setDimensions({ width: newTotalW, height: appliedDims.faceH });
          appliedDims = { ...appliedDims, spineW: savedSpW };
          setFaceDims(appliedDims);
        }

        if (json.background && !json.backgroundColor) {
          json.backgroundColor = json.background;
          delete json.background;
        }

        try {
          await c.loadFromJSON(json);
          c.backgroundColor = bgColor;

          // Restore `name` on canvas objects.
          // For new saves: Fabric v6 restores `name` automatically via its constructor
          // (name is in objectProps passed to new FabricImage()), so this is a safety net.
          // For async-safe matching we use src for images instead of index order.
          const rawObjs = json.objects || [];

          // Pass 1: src-based matching for image objects (order-independent)
          rawObjs.forEach((rawObj) => {
            if (!rawObj.name || rawObj.type !== 'image' || !rawObj.src) return;
            const match = c.getObjects().find(
              (o) => o.type === 'image' && !o.name &&
                     (typeof o.getSrc === 'function' ? o.getSrc() : o._element?.src) === rawObj.src
            );
            if (match) match.name = rawObj.name;
          });

          // Pass 2: index-based fallback for non-image named objects
          c.getObjects().forEach((obj, i) => {
            if (!obj.name && rawObjs[i]?.name) obj.name = rawObjs[i].name;
            // Restore _collabId (annotation thread ID) — Fabric v6 does not auto-restore underscore properties
            if (!obj._collabId && rawObjs[i]?._collabId) obj._collabId = rawObjs[i]._collabId;
            // Restore _locked state and apply Fabric lock properties
            if (rawObjs[i]?._locked) {
              obj._locked = true;
              obj.set({
                lockMovementX: true,
                lockMovementY: true,
                lockRotation: true,
                lockScalingX: true,
                lockScalingY: true,
                lockSkewingX: true,
                lockSkewingY: true,
                hasControls: false,
              });
              if (obj.type === 'textbox' || obj.type === 'i-text') {
                obj.set({ editable: false });
              }
            }
          });

          // Pass 3: old-save fallback — hasIsbn flag but no name in JSON
          // Match by scaleX/scaleY 0.55 (the hardcoded scale used when placing the barcode)
          if (foundIsbn && !c.getObjects().some((o) => o.name === 'isbn-barcode')) {
            const byScale = c.getObjects().filter(
              (o) => o.type === 'image' && !o.name &&
                     Math.abs((o.scaleX || 1) - 0.55) < 0.01 &&
                     Math.abs((o.scaleY || 1) - 0.55) < 0.01
            );
            if (byScale.length >= 1) {
              byScale[0].name = 'isbn-barcode';
            } else {
              // Last resort: single unnamed image
              const unnamed = c.getObjects().filter((o) => o.type === 'image' && !o.name);
              if (unnamed.length === 1) unnamed[0].name = 'isbn-barcode';
            }
          }

          // Sync final hasIsbn state from actual named objects
          const isbnPresent = c.getObjects().some((o) => o.name === 'isbn-barcode');
          c._hasIsbn = isbnPresent;
          setHasIsbn(isbnPresent);

          // Pass 4: Tag & lock freehand drawing strokes (old saves lack names)
          const SYSTEM_OBJ_NAMES = ['bg-front','bg-spine','bg-back','margin-guide','isbn-barcode'];

          // Pass 4a: Lock bg-rects and remove stacked orphan bg-rects per face.
          // Old saves often have multiple unnamed rects created each time the
          // user changed the background color (name got lost on save/reload).
          // For each face: keep the properly named rect (or topmost unnamed match
          // if no named one exists), remove the rest.
          const fW = appliedDims?.faceW  || DEFAULT_FACE_W;
          const sW = appliedDims?.spineW || DEFAULT_SPINE_W;
          const fH = appliedDims?.faceH  || DEFAULT_FACE_H;
          const bgFaceRegions = {
            'bg-back':  { left: 0,        width: fW,  height: fH },
            'bg-spine': { left: fW,       width: sW,  height: fH },
            'bg-front': { left: fW + sW,  width: fW,  height: fH },
          };

          Object.entries(bgFaceRegions).forEach(([bgName, region]) => {
            const named = c.getObjects().find((o) => o.name === bgName);

            // Find unnamed rects that sit at this face's position/size (orphans)
            const orphans = c.getObjects().filter((o) => {
              if (o === named) return false;            // don't touch the named one
              if (o.name) return false;                 // has a name — not an orphan
              if (o.type !== 'rect') return false;
              const oL = Math.round(o.left || 0);
              const oT = Math.round(o.top  || 0);
              const oW = Math.round((o.width || 0) * (o.scaleX || 1));
              const oH = Math.round((o.height || 0) * (o.scaleY || 1));
              return Math.abs(oL - region.left) < 10 &&
                     oT < 10 &&
                     Math.abs(oW - region.width) < 30 &&
                     Math.abs(oH - region.height) < 30;
            });

            if (named) {
              // Named rect exists — remove all orphans, snap geometry, clear stale clips.
              // Stale clipPaths can occur when the rect was briefly nameless during
              // undo/redo (loadFromJSON fires object:added before name is restored).
              named.clipPath = undefined;
              named.set({
                ...region,
                top: 0, scaleX: 1, scaleY: 1,
                visible: true, opacity: 1,
                selectable: false, evented: false,
              });
              orphans.forEach((o) => c.remove(o));
            } else if (orphans.length > 0) {
              // No named rect — keep the LAST one (topmost / most recent color),
              // name it properly, remove the rest
              const keep = orphans[orphans.length - 1];
              keep.clipPath = undefined;
              keep.set({ ...region, top: 0, scaleX: 1, scaleY: 1, name: bgName, selectable: false, evented: false });
              orphans.slice(0, -1).forEach((o) => c.remove(o));
            }
          });

          // Pass 4b: Re-apply saved per-face background colors.
          // This is a safety net for when bg-rect name restoration fails — the
          // saved `faceBgColors` backup in the design JSON lets us recreate a
          // missing bg-rect or restore a fill that was lost. Only applies if
          // the stored color differs from what's currently on the bg-rect.
          if (json.faceBgColors && typeof json.faceBgColors === 'object') {
            c._faceBgColors = { ...json.faceBgColors };
            Object.entries(json.faceBgColors).forEach(([face, color]) => {
              if (!color) return;
              const bgName = `bg-${face}`;
              const region = bgFaceRegions[bgName];
              if (!region) return;
              const existing = c.getObjects().find((o) => o.name === bgName);
              if (existing) {
                if (existing.fill !== color) {
                  existing.set({ fill: color });
                  existing.dirty = true;
                }
              } else {
                const rect = new fabric.Rect({
                  ...region,
                  top: 0,
                  fill: color,
                  selectable: false,
                  evented: false,
                  name: bgName,
                });
                c.add(rect);
                c.sendObjectToBack(rect);
              }
            });
          }

          c.getObjects().forEach((o) => {
            // Already tagged
            if (o.name === 'draw-stroke' || o.name === 'eraser-path') {
              o.set({ selectable: false, evented: false });
              return;
            }
            // Skip system / named design objects
            if (o.name && SYSTEM_OBJ_NAMES.includes(o.name)) return;
            if (o.name && o.name.startsWith('bg-')) return;
            // Untagged paths = pencil/marker strokes
            if (o.type === 'path' && !o.name) {
              o.set({ name: 'draw-stroke', selectable: false, evented: false });
              return;
            }
            // Untagged groups = CircleBrush paint blobs
            if (o.type === 'group' && !o.name) {
              o.set({ name: 'draw-stroke', selectable: false, evented: false });
              return;
            }
            // Untagged circles: modern shapes (from the shape tool) are also
            // unnamed in older saves. Only a legacy CircleBrush single-dot
            // (tiny, no stroke) should be locked as a paint stroke. A circle
            // with a stroke, a real radius, or an annotation id is a user shape
            // — recover it by naming it so it stays selectable.
            if (o.type === 'circle' && !o.name) {
              const isLegacyDot = !o._collabId &&
                (!o.stroke || (o.strokeWidth || 0) === 0) &&
                (o.radius || 0) < 10;
              if (isLegacyDot) {
                o.set({ name: 'draw-stroke', selectable: false, evented: false });
              } else {
                o.set({ name: 'shape-circle' });
              }
            }
          });

          // Pass 4c: Correct stale absolute-positioned clipPaths that don't match
          // the face the object actually sits on. Caused by applyFaceClip firing
          // during undo/redo while the user was in a different view than where
          // the object was originally placed (resulting in an invisible object).
          const faceRegionByCenter = (cx) => {
            if (cx < fW)          return { left: 0,        top: 0, width: fW,  height: fH };
            if (cx < fW + sW)     return { left: fW,       top: 0, width: sW,  height: fH };
            return                       { left: fW + sW,  top: 0, width: fW,  height: fH };
          };
          c.getObjects().forEach((o) => {
            if (o.name && (SYSTEM_OBJ_NAMES.includes(o.name) || o.name.startsWith('bg-'))) return;
            if (o.name === 'draw-stroke' || o.name === 'eraser-path') return;
            const cp = o.clipPath;
            if (!cp || !cp.absolutePositioned) return;
            const w = (o.width || 0) * (o.scaleX || 1);
            const h = (o.height || 0) * (o.scaleY || 1);
            const cx = (o.left || 0) + w / 2;
            const cy = (o.top  || 0) + h / 2;
            const clipL = cp.left || 0;
            const clipR = clipL + (cp.width || 0);
            const clipT = cp.top || 0;
            const clipB = clipT + (cp.height || 0);
            // Stale means the object does not TOUCH its clip at all — not that
            // its centre sits outside it.
            //
            // The centre test broke anything spanning more than one face. A
            // full wraparound design is centred over the spine while being
            // legitimately clipped to the front, so it was judged stale and
            // re-scoped to the 100px spine: the front cover then rendered
            // empty, and the autosave uploaded that over the stored artwork.
            // Measured — `applyFaceClip` set front, and this re-set it to
            // {left: 561, width: 100} on the very next pass.
            //
            // An object that genuinely sits on another face shares no pixels
            // with its clip, which is the case this was written for and which
            // the intersection test still catches.
            const objR = (o.left || 0) + w;
            const objB = (o.top || 0) + h;
            const disjoint = objR <= clipL || (o.left || 0) >= clipR
                          || objB <= clipT || (o.top || 0) >= clipB;
            if (disjoint) {
              const correct = faceRegionByCenter(cx);
              o.clipPath = new fabric.Rect({ ...correct, absolutePositioned: true });
            }
          });

          // Pass 4d: Ensure all clipPaths are proper Fabric instances.
          // loadFromJSON in Fabric v6 can leave clipPath as a plain JS object
          // (no prototype methods) when deserialization partially fails. Calling
          // .transform() on a plain object during mouse hit-testing crashes.
          c.getObjects().forEach((o) => {
            const cp = o.clipPath;
            if (!cp || typeof cp.transform === 'function') return;
            o.clipPath = new fabric.Rect({
              left: cp.left || 0,
              top: cp.top || 0,
              width: cp.width || 0,
              height: cp.height || 0,
              absolutePositioned: cp.absolutePositioned || false,
            });
          });

          // Pass 5: Detect and correct dimension mismatch on load.
          //
          // Strategy: infer the dims the design was ACTUALLY DRAWN AT by reading
          // the geometry of bg-rects — NOT from json.faceDims (which reflects
          // React state and may lag behind actual object positions).
          //
          // bg-back is ALWAYS at left≈0; its width is the true saved faceW.
          // We name/lock any unnamed full-height rect we find here as well.
          const currentFW = fW, currentFH = fH, currentSW = sW;

          // Find a full-height background rect by raw geometry (name-independent).
          const isFullHeightRect = (o, expectedLeft, leftTol = 20) => {
            if (o.type !== 'rect') return false;
            const oT = o.top || 0;
            const oH = (o.height || 0) * (o.scaleY || 1);
            const oW = (o.width  || 0) * (o.scaleX || 1);
            return Math.abs((o.left || 0) - expectedLeft) < leftTol &&
                   oT < 10 && oH > 80 && oW > 20;
          };

          // bg-back is always anchored to left=0 — safest signal for saved faceW
          const bgBackRaw = c.getObjects().find(o => isFullHeightRect(o, 0));
          const srcFW = bgBackRaw ? Math.round((bgBackRaw.width  || 0) * (bgBackRaw.scaleX || 1)) : null;
          const srcFH = bgBackRaw ? Math.round((bgBackRaw.height || 0) * (bgBackRaw.scaleY || 1)) : null;

          // Spine raw: full-height rect at left≈srcFW
          const bgSpineRaw = srcFW != null
            ? c.getObjects().find(o => isFullHeightRect(o, srcFW, 20) && o !== bgBackRaw)
            : null;
          const srcSW = bgSpineRaw
            ? Math.round((bgSpineRaw.width || 0) * (bgSpineRaw.scaleX || 1))
            : currentSW;

          const hasMismatch = srcFW != null && srcFH != null && (
            Math.abs(srcFW - currentFW) > 5 || Math.abs(srcFH - currentFH) > 5 ||
            Math.abs(srcSW - currentSW) > 5
          );

          if (hasMismatch) {
            const ratioFace  = currentFW / srcFW;
            const ratioSpine = currentSW / srcSW;
            const ratioH     = currentFH / srcFH;
            const oldSpineEnd = srcFW + srcSW;
            const newSpineEnd = currentFW + currentSW;

            // Helper: is this object one of the raw bg-rects we detected?
            const isBgRaw = (o) => o === bgBackRaw || o === bgSpineRaw ||
              // front bg: full-height rect at left≈srcFW+srcSW
              isFullHeightRect(o, srcFW + srcSW, 20);

            c.getObjects().forEach((obj) => {
              if (obj.name === 'margin-guide') return;

              // Snap bg-rects (named OR detected by geometry) to new face regions
              const isBgNamed = ['bg-back','bg-spine','bg-front'].includes(obj.name);
              if (isBgNamed || isBgRaw(obj)) {
                const l = obj.left || 0;
                let region;
                if (isBgNamed) {
                  region = obj.name === 'bg-back'  ? { left: 0,           width: currentFW }
                         : obj.name === 'bg-spine' ? { left: currentFW,   width: currentSW }
                         :                           { left: newSpineEnd,  width: currentFW };
                } else {
                  // Geometry-based: assign by old position
                  region = l < srcFW - 10       ? { left: 0,           width: currentFW }
                         : l < oldSpineEnd - 10 ? { left: currentFW,   width: currentSW }
                         :                        { left: newSpineEnd,  width: currentFW };
                }
                obj.set({ ...region, top: 0, height: currentFH, scaleX: 1, scaleY: 1, selectable: false, evented: false });
                obj.setCoords();
                return;
              }

              // General objects: proportional scale relative to their face
              const w  = (obj.width || 0) * (obj.scaleX || 1);
              const cx = (obj.left  || 0) + w / 2;
              let oldFL, newFL, ratioX;
              if (cx < srcFW) {
                oldFL = 0;           newFL = 0;           ratioX = ratioFace;
              } else if (cx < oldSpineEnd) {
                oldFL = srcFW;       newFL = currentFW;   ratioX = ratioSpine;
              } else {
                oldFL = oldSpineEnd; newFL = newSpineEnd; ratioX = ratioFace;
              }
              const newLeft = newFL + ((obj.left || 0) - oldFL) * ratioX;
              const newTop  = (obj.top || 0) * ratioH;

              if (obj.type === 'textbox') {
                obj.set({ left: newLeft, top: newTop, width: (obj.width||0)*ratioX, fontSize: (obj.fontSize||16)*ratioH, scaleX: 1, scaleY: 1 });
              } else {
                obj.set({ left: newLeft, top: newTop, scaleX: (obj.scaleX||1)*ratioX, scaleY: (obj.scaleY||1)*ratioH });
              }
              if (obj.clipPath) {
                // Which face this object is assigned to ("Appears On") is read
                // from the clip it ALREADY has, not re-derived from where its
                // centre happens to fall.
                //
                // The centre test is wrong for anything that spans faces, and
                // wrong in the worst direction: a full wraparound design has
                // its centre over the spine, so it was being re-scoped from
                // "the whole cover" to a 100px strip. The front cover then
                // rendered empty, and the autosave uploaded that emptiness over
                // the stored artwork. Measured on a real cover: `applyFaceClip`
                // correctly set front, and this pass replaced it with
                // {left: 561, width: 100}.
                const clipL = obj.clipPath.left || 0;
                const r = clipL < srcFW - 10       ? { left: 0,           width: currentFW }
                        : clipL < oldSpineEnd - 10 ? { left: currentFW,   width: currentSW }
                        :                            { left: newSpineEnd, width: currentFW };
                obj.clipPath = new fabric.Rect({ ...r, top: 0, height: currentFH, absolutePositioned: true });
              }
              obj.setCoords();
            });
          }

          c.requestRenderAll();
        } catch (_e) {
          try {
            const objs = await fabric.util.enlivenObjects(json.objects || []);
            objs.forEach((o) => c.add(o));
            c.backgroundColor = bgColor;
            c.requestRenderAll();
          } catch (fallbackErr) {
            console.error('[useCanvasSetup] Fallback load failed:', fallbackErr);
          }
        }

        // Initialise shape name counters from the loaded objects so new shapes
        // continue from the highest existing number (e.g. if shape-circle3 exists,
        // the next circle will be shape-circle4). Stored on the canvas instance.
        {
          const counters = {};
          c.getObjects().forEach(o => {
            if (!o.name) return;
            const m = o.name.match(/^shape-([a-z]+)(\d+)$/);
            if (m) {
              const type = m[1], num = parseInt(m[2], 10);
              if (!counters[type] || num > counters[type]) counters[type] = num;
            }
          });
          c._shapeCounters = counters;
        }

        c._rvHydrating = false;
          setIsHydrating(false);

        if (versionCollabRole === 'view_only' && !roleAuthor) {
          c.selection = false;
          c.defaultCursor = 'default';
          c.hoverCursor = 'default';
          c.forEachObject((obj) => {
            obj.selectable = false;
            obj.evented = false;
            obj.hoverCursor = 'default';
          });
          c.requestRenderAll();
        }
      } catch (e) {
        if (!cancelled) {
          console.error('[useCanvasSetup] Load failed:', e);
          c._rvHydrating = false;
          setIsHydrating(false);
        }
      }
    })();

    return () => { cancelled = true; c.dispose(); didInit.current = false; };
  }, [draftId, urlVersionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    canvasRef,
    canvas,
    currentVersionId,
    activeVersionName,
    isAuthor,
    collaboratorRole,
    readOnly,
    versionData,
    setVersionData,
    isHydrating,
    hasIsbn,
    hasCoverImage,
    faceDims, // { faceW, faceH, spineW }
    setFaceDims,
  };
}
